import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}



/* ===== Color Swatch Image Thumbnails for Horizon v6 ===== */
(function () {
  let productCache = null;

  async function getProduct() {
    if (productCache) return productCache;
    try {
      const handle = location.pathname.split('/products/')[1]?.split('?')[0];
      const res = await fetch(`/products/${handle}.js`);
      productCache = await res.json();
      return productCache;
    } catch (e) { return null; }
  }

  function switchMainImage(variant) {
    if (!variant?.featured_image) return;
    const mediaId = String(variant.featured_image.id);
    const thumb = document.querySelector(`img[data-media-id="${mediaId}"], [data-media-id="${mediaId}"] img`);
    if (thumb) { thumb.closest('[data-media-id]')?.click(); return; }
    const mediaDiv = document.querySelector(`.product-media[data-media-id="${mediaId}"]`);
    if (mediaDiv) { mediaDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); return; }
    const mainImg = document.querySelector('.product__media img, [data-media-type="image"] img');
    if (mainImg && variant.featured_image.src) mainImg.src = variant.featured_image.src;
  }

  async function refreshCart(addedItem) {
    try {
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      document.querySelectorAll('.cart-bubble__text--count').forEach(el => { el.textContent = cart.item_count; });
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: addedItem, sourceId: String(addedItem.variant_id), data: cart }
      }));
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && typeof cartDrawer.open === 'function') cartDrawer.open();
    } catch (e) {}
  }

  // 获取当前选中的非Color选项值（如 Size/Weight）
  function getSelectedNonColorOptions(variantPicker, colorOptionIndex) {
    const product = productCache;
    if (!product) return {};
    const selectedMap = {};
    variantPicker.querySelectorAll('fieldset.variant-option').forEach((fs, fsIndex) => {
      if (fsIndex === colorOptionIndex) return; // 跳过 color fieldset
      const checked = fs.querySelector('input[type="radio"]:checked, select');
      if (!checked) return;
      let val = null;
      if (checked.tagName === 'SELECT') {
        val = checked.options[checked.selectedIndex]?.value;
      } else {
        val = checked.value;
      }
      if (val) selectedMap[fsIndex] = val;
    });
    return selectedMap;
  }

  // 根据当前选中的其他选项，找到该颜色对应的变体价格
  function getPriceForColor(colorVariants, variantPicker, colorFieldsetIndex) {
    const product = productCache;
    if (!product) return colorVariants[0]?.price ?? 0;

    const selectedNonColor = getSelectedNonColorOptions(variantPicker, colorFieldsetIndex);
    const nonColorKeys = Object.keys(selectedNonColor);

    if (nonColorKeys.length === 0) {
      // 没有其他选项，取最低价
      return Math.min(...colorVariants.map(v => v.price));
    }

    // 找到匹配所有其他选项的变体
    const matched = colorVariants.find(variant => {
      return nonColorKeys.every(fsIndex => {
        const optionKey = `option${parseInt(fsIndex) + 1}`;
        return variant[optionKey] === selectedNonColor[fsIndex];
      });
    });

    if (matched) return matched.price;
    // 找不到精确匹配，取最低价
    return Math.min(...colorVariants.map(v => v.price));
  }

  // 更新所有 swatch 卡片的价格
  // function updateSwatchPrices(variantPicker, colorFieldsetIndex, colorIndex) {
  //   const product = productCache;
  //   if (!product) return;

  //   const formatPrice = (cents) => (cents / 100).toLocaleString('en-US', { style: 'currency', currency: product.currency || 'USD' });

  //   variantPicker.querySelectorAll('.swatch-thumb-card').forEach(card => {
  //     const colorName = card.dataset.color;
  //     if (!colorName) return;

  //     const colorVariants = product.variants.filter(v => {
  //       const colorVal = colorIndex >= 0 ? v[`option${colorIndex + 1}`] : v.option1;
  //       return colorVal === colorName;
  //     });
  //     if (!colorVariants.length) return;

  //     const priceEl = card.querySelector('.swatch-thumb-price');
  //     const compareEl = card.querySelector('.swatch-thumb-price-compare');
  //     if (!priceEl) return;

  //     const price = getPriceForColor(colorVariants, variantPicker, colorFieldsetIndex);

  //     // 找到对应变体的 compare_at_price
  //     const selectedNonColor = getSelectedNonColorOptions(variantPicker, colorFieldsetIndex);
  //     const nonColorKeys = Object.keys(selectedNonColor);
  //     let matchedVariant = colorVariants.find(variant =>
  //       nonColorKeys.every(fsIndex => variant[`option${parseInt(fsIndex) + 1}`] === selectedNonColor[fsIndex])
  //     ) || colorVariants[0];

  //     priceEl.textContent = formatPrice(price);

  //     if (compareEl) {
  //       if (matchedVariant.compare_at_price && matchedVariant.compare_at_price > price) {
  //         compareEl.textContent = formatPrice(matchedVariant.compare_at_price);
  //         compareEl.style.display = '';
  //       } else {
  //         compareEl.textContent = '';
  //         compareEl.style.display = 'none';
  //       }
  //     }
  //   });
  // }
function updateSwatchPrices(variantPicker, colorFieldsetIndex, colorIndex) {
  const product = productCache;
  if (!product) return;

  const formatPrice = (cents) => (cents / 100).toLocaleString('en-US', { style: 'currency', currency: product.currency || 'USD' });
  const selectedNonColor = getSelectedNonColorOptions(variantPicker, colorFieldsetIndex);
  const nonColorKeys = Object.keys(selectedNonColor);
  const hasNonColorSelection = nonColorKeys.length > 0;

  variantPicker.querySelectorAll('.swatch-thumb-card').forEach(card => {
    const colorName = card.dataset.color;
    if (!colorName) return;

    const colorVariants = product.variants.filter(v => {
      const colorVal = colorIndex >= 0 ? v[`option${colorIndex + 1}`] : v.option1;
      return colorVal === colorName;
    });
    if (!colorVariants.length) return;

    let matchedVariant;
    if (hasNonColorSelection) {
      matchedVariant = colorVariants.find(variant =>
        nonColorKeys.every(fsIndex => variant[`option${parseInt(fsIndex) + 1}`] === selectedNonColor[fsIndex])
      );
    }
    const displayVariant = matchedVariant || colorVariants[0];

    const isSoldOut = hasNonColorSelection
      ? (!matchedVariant || !matchedVariant.available)
      : colorVariants.every(v => !v.available);

    // 更新售罄样式和徽章
    card.classList.toggle('is-sold-out', isSoldOut);
    const atcBtn = card.querySelector('.swatch-thumb-atc');
    if (atcBtn) atcBtn.disabled = isSoldOut;

    const imgWrap = card.querySelector('.swatch-thumb-img-wrap');
    let badge = imgWrap?.querySelector('.swatch-thumb-sold-out-badge');
    if (isSoldOut && !badge) {
      badge = document.createElement('div');
      badge.className = 'swatch-thumb-sold-out-badge';
      badge.textContent = 'Sold Out';
      imgWrap.appendChild(badge);
    } else if (!isSoldOut && badge) {
      badge.remove();
    }

    // 更新价格
    const priceEl = card.querySelector('.swatch-thumb-price');
    const compareEl = card.querySelector('.swatch-thumb-price-compare');
    if (!priceEl) return;

    const price = displayVariant.price;
    priceEl.textContent = formatPrice(price);

    if (compareEl) {
      if (displayVariant.compare_at_price && displayVariant.compare_at_price > price) {
        compareEl.textContent = formatPrice(displayVariant.compare_at_price);
        compareEl.style.display = '';
      } else {
        compareEl.textContent = '';
        compareEl.style.display = 'none';
      }
    }
  });
}
async function applySwatchThumbnails(variantPicker) {
  if (variantPicker.querySelector('.swatch-thumb-grid')) return;

  const product = await getProduct();
  if (!product?.variants) {
    variantPicker.querySelectorAll('label.variant-option__button-label')
      .forEach(el => el.style.removeProperty('display'));
    return;
  }

  const colorIndex = (product.options || []).findIndex(opt =>
    /color|colour|颜色/i.test(typeof opt === 'string' ? opt : opt.name)
  );

  if (!document.getElementById('swatch-thumb-style')) {
    const style = document.createElement('style');
    style.id = 'swatch-thumb-style';
    style.textContent = `
      .swatch-thumb-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; width: 100%; margin-top: 8px; }
      @media (max-width: 749px) { .swatch-thumb-grid { grid-template-columns: repeat(2, 1fr); } }
      .swatch-thumb-card { display: flex; flex-direction: column; border: 2px solid #e0e0e0; border-radius: 8px; overflow: hidden; cursor: pointer; background: #fff; transition: border-color 0.2s; }
      .swatch-thumb-card:hover { border-color: #999; }
      .swatch-thumb-card.is-selected { border-color: #000; }
      .swatch-thumb-card.is-sold-out { opacity: 0.85; }
      .swatch-thumb-img-wrap { width: 100%; aspect-ratio: 1 / 1; overflow: hidden; position: relative; }
      .swatch-thumb-img-wrap img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.3s; }
      .swatch-thumb-card:hover .swatch-thumb-img-wrap img { transform: scale(1.05); }
      .swatch-thumb-sold-out-badge { position: absolute; bottom: 0; left: 0; right: 0; background: rgba(0,0,0,0.55); color: #fff; font-size: 11px; font-weight: 600; text-align: center; padding: 4px 0; text-transform: uppercase; }
      .swatch-thumb-info { padding: 6px 8px 8px; display: flex; flex-direction: column; gap: 2px; }
      .swatch-thumb-name { font-size: 11px; color: #333; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .swatch-thumb-name.is-sold-out { text-decoration: line-through; color: #999; }
      .swatch-thumb-price-row { display: flex; align-items: center; justify-content: space-between; gap: 4px; margin-top: 2px; }
      .swatch-thumb-prices { display: flex; flex-direction: column; }
      .swatch-thumb-price { font-size: 12px; font-weight: 600; color: #111; }
      .swatch-thumb-price-compare { font-size: 11px; color: #999; text-decoration: line-through; }
      .swatch-thumb-atc { width: 26px; height: 26px; border-radius: 50%; background: #111; color: #fff; border: none; font-size: 18px; cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: background 0.2s; padding: 0; line-height: 1; }
      .swatch-thumb-atc:hover { background: #444; }
      .swatch-thumb-atc:disabled { background: #ccc; cursor: not-allowed; }
      variant-picker .swatch-color-fieldset label.variant-option__button-label { display: none !important; }
    `;
    document.head.appendChild(style);
  }

  let colorFieldset = null;
  let colorFieldsetIndex = -1;
  variantPicker.querySelectorAll('fieldset.variant-option').forEach((fs, idx) => {
    const legend = fs.querySelector('legend');
    if (legend && /color|colour|颜色/i.test(legend.textContent)) {
      colorFieldset = fs;
      colorFieldsetIndex = idx;
    }
  });
  if (!colorFieldset) {
    colorFieldset = variantPicker.querySelector('fieldset.variant-option');
    colorFieldsetIndex = 0;
  }
  if (!colorFieldset) return;
  colorFieldset.classList.add('swatch-color-fieldset');

  const grid = document.createElement('div');
  grid.className = 'swatch-thumb-grid';
  colorFieldset.appendChild(grid);

  const formatPrice = (cents) => (cents / 100).toLocaleString('en-US', { style: 'currency', currency: product.currency || 'USD' });

  colorFieldset.querySelectorAll('label.variant-option__button-label').forEach(label => {
    const span = label.querySelector('.variant-option__button-label__text');
    if (!span) return;
    const colorName = span.textContent.trim();
    const input = label.querySelector('input[type="radio"]');

    const colorVariants = product.variants.filter(v => {
      const colorVal = colorIndex >= 0 ? v[`option${colorIndex + 1}`] : v.option1;
      return colorVal === colorName;
    });
    if (!colorVariants.length) return;

    const matched = colorVariants[0];
    const imgSrc = matched.featured_image
      ? matched.featured_image.src.replace(/(\.[^.?]+)(\?.*)?$/, '_300x300_crop_center$1')
      : null;

    // 创建卡片，不预设售罄状态，统一交给 updateSwatchPrices 处理
    const card = document.createElement('div');
    card.className = 'swatch-thumb-card' + (input?.checked ? ' is-selected' : '');
    card.dataset.color = colorName;
    card.dataset.variantId = matched.id;

    // 初始 innerHTML：无 Sold Out 徽章，价格用占位，按钮不禁用
    card.innerHTML = `
      <div class="swatch-thumb-img-wrap">
        ${imgSrc ? `<img src="${imgSrc}" alt="${colorName}" loading="lazy">` : ''}
      </div>
      <div class="swatch-thumb-info">
        <span class="swatch-thumb-name">${colorName}</span>
        <div class="swatch-thumb-price-row">
          <div class="swatch-thumb-prices">
            <span class="swatch-thumb-price-compare" style="display:none"></span>
            <span class="swatch-thumb-price"></span>
          </div>
          <button class="swatch-thumb-atc">+</button>
        </div>
      </div>
    `;

    card.addEventListener('mouseenter', () => switchMainImage(matched));
    card.addEventListener('click', e => {
      if (e.target.closest('.swatch-thumb-atc')) return;
      input?.click();
      grid.querySelectorAll('.swatch-thumb-card').forEach(c => c.classList.remove('is-selected'));
      card.classList.add('is-selected');
      switchMainImage(matched);
    });

    const atcBtn = card.querySelector('.swatch-thumb-atc');
    if (atcBtn) {
      atcBtn.addEventListener('click', async e => {
        e.stopPropagation();
        atcBtn.disabled = true;
        atcBtn.textContent = '…';
        try {
          const selectedNonColor = getSelectedNonColorOptions(variantPicker, colorFieldsetIndex);
          const nonColorKeys = Object.keys(selectedNonColor);
          const currentVariant = colorVariants.find(variant =>
            nonColorKeys.every(fsIndex => variant[`option${parseInt(fsIndex) + 1}`] === selectedNonColor[fsIndex])
          ) || colorVariants[0];
          if (!currentVariant || !currentVariant.available) {
            atcBtn.textContent = '!';
            setTimeout(() => { atcBtn.textContent = '+'; atcBtn.disabled = false; }, 1500);
            return;
          }
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: currentVariant.id, quantity: 1 })
          });
          if (res.ok) {
            const addedItem = await res.json();
            atcBtn.textContent = '✓';
            atcBtn.style.background = '#2a7a2a';
            await refreshCart(addedItem);
            setTimeout(() => { atcBtn.textContent = '+'; atcBtn.style.background = ''; atcBtn.disabled = false; }, 1500);
          } else throw new Error();
        } catch {
          atcBtn.textContent = '!';
          setTimeout(() => { atcBtn.textContent = '+'; atcBtn.disabled = false; }, 1500);
        }
      });
    }

    grid.appendChild(card);
  });

  // ✅ 所有卡片添加完毕后，立即根据当前选项刷新状态（售罄、价格、徽章）
  updateSwatchPrices(variantPicker, colorFieldsetIndex, colorIndex);

  // ✅ 监听非颜色选项变化，动态更新所有颜色卡片
  variantPicker.addEventListener('change', (e) => {
    if (e.target.closest('fieldset') === colorFieldset) return;
    updateSwatchPrices(variantPicker, colorFieldsetIndex, colorIndex);
  });
}


  function observeVariantPicker() {
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) return;

    applySwatchThumbnails(variantPicker);

    let morphing = false;
    new MutationObserver(() => {
      if (morphing) return;
      if (!variantPicker.querySelector('.swatch-thumb-grid')) {
        morphing = true;
        setTimeout(() => { applySwatchThumbnails(variantPicker); morphing = false; }, 50);
      }
    }).observe(variantPicker, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeVariantPicker);
  } else {
    observeVariantPicker();
  }
})();
/* ===== End Color Swatch Image Thumbnails v6 ===== */



/* ===== CaiLab: Collapse swatch cards beyond 10 ===== */
(function() {
  const LIMIT = 10;

  function collapseSwatches() {
    const grid = document.querySelector('.swatch-thumb-grid');
    if (!grid) return;
    if (grid.dataset.cailabInit) return; // 防止重复执行
    grid.dataset.cailabInit = '1';

    const cards = Array.from(grid.querySelectorAll('.swatch-thumb-card'));
    if (cards.length <= LIMIT) return;

    // 隐藏第11个以后的卡片
    cards.forEach((card, i) => {
      if (i >= LIMIT) card.style.display = 'none';
    });

    // 创建展开按钮
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = 'View all products（' + cards.length + '）▼';
btn.style.cssText = 'display:inline-flex;align-items:center;gap:6px;margin-top:8px;padding:8px 20px;background:#fff;color:#333;border:2px solid #333;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;letter-spacing:0.03em;';

const btnWrap = document.createElement('div');
btnWrap.style.cssText = 'display:block;width:100%;margin-top:16px;clear:both;';
btnWrap.appendChild(btn);
const fieldset = grid.closest('fieldset') || grid.parentElement;
fieldset.insertAdjacentElement('afterend', btnWrap);




    btn.onmouseenter = function() { btn.style.background='#fff'; btn.style.color='#111'; };
    btn.onmouseleave = function() { btn.style.background='#111'; btn.style.color='#fff'; };

    let expanded = false;
    btn.onclick = function() {
      expanded = !expanded;
      cards.forEach((card, i) => {
        if (i >= LIMIT) card.style.display = expanded ? '' : 'none';
      });
      btn.textContent = expanded ? 'Put ▲' : 'View all products（' + cards.length + '）▼';
    };


  }

  // 轮询等待 grid 渲染完成（因为是异步注入的）
  function waitForGrid() {
    const grid = document.querySelector('.swatch-thumb-grid');
    if (grid && grid.querySelectorAll('.swatch-thumb-card').length > 0) {
      collapseSwatches();
    } else {
      setTimeout(waitForGrid, 200);
    }
  }

  // 变体切换后重新执行
  document.addEventListener('variant:updated', function() {
    setTimeout(collapseSwatches, 100);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForGrid);
  } else {
    waitForGrid();
  }
})();
/* ===== End CaiLab ===== */
